import React, { useCallback, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import GavelIcon from '@mui/icons-material/Gavel';
import CalculateIcon from '@mui/icons-material/Calculate';
import {
  ASSESSMENT_LABELS,
  AssessmentType,
  createRafAssessment,
} from '../../../actions/matters/createRafAssessment';
import type { AssessmentRecord } from '../../../types/matter';
import { generateDownloadUrl } from '../../../actions/files/generateDownloadUrl';
import { uploadFilesToS3 } from '../../../utils/fileUploadHelper';
import AssessmentUploadModal from '../../../components/matters/AssessmentUploadModal';
import UploadLoaderModal from '../../../components/modals/UploadLoaderModal';

interface AssessmentsTabProps {
  matterId: string;
  meritsAssessment: AssessmentRecord | undefined;
  quantumAssessment: AssessmentRecord | undefined;
  userSub: string | null;
  onRefresh: () => void;
}

const ASSESSMENT_DESCRIPTIONS: Record<AssessmentType, string> = {
  merits:
    'A structured legal opinion on liability — covering negligence, apportionment, hit-and-run rules, and procedural defences under the RAF Act.',
  quantum:
    'An assessment of damages — past and future medical expenses, loss of earnings, general damages, and loss of support — based on the uploaded medical and financial records.',
};

const ASSESSMENT_ICONS: Record<AssessmentType, React.ReactNode> = {
  merits: <GavelIcon sx={{ fontSize: 28, color: 'var(--teal)' }} />,
  quantum: <CalculateIcon sx={{ fontSize: 28, color: 'var(--teal)' }} />,
};

const AssessmentsTab: React.FC<AssessmentsTabProps> = ({
  matterId,
  meritsAssessment,
  quantumAssessment,
  userSub,
  onRefresh,
}) => {
  const [openModalFor, setOpenModalFor] = useState<AssessmentType | null>(null);
  const [error, setError] = useState('');

  // Upload-loader modal state
  const [loaderOpen, setLoaderOpen] = useState(false);
  const [loaderFiles, setLoaderFiles] = useState<File[]>([]);
  const [activeJobId, setActiveJobId] = useState('');
  const [activeAssessmentType, setActiveAssessmentType] = useState<AssessmentType | null>(null);
  const [pendingOptions, setPendingOptions] = useState<{
    representedParty?: string;
    additionalContext?: string;
  }>({});

  const recordFor = useCallback(
    (type: AssessmentType): AssessmentRecord | undefined =>
      type === 'merits' ? meritsAssessment : quantumAssessment,
    [meritsAssessment, quantumAssessment],
  );

  const handleStartGeneration = useCallback(
    (
      type: AssessmentType,
      files: File[],
      options: { representedParty?: string; additionalContext?: string },
    ) => {
      setError('');
      setActiveAssessmentType(type);
      setLoaderFiles(files);
      setPendingOptions(options);
      setActiveJobId('');
      setOpenModalFor(null);
      setLoaderOpen(true);
    },
    [],
  );

  const handleLoaderUpload = useCallback(
    async (
      filesToUpload: File[],
      progressCallback: (progress: number) => void,
    ): Promise<string> => {
      if (!activeAssessmentType) {
        throw new Error('No assessment type selected.');
      }
      const uploaded = await uploadFilesToS3(filesToUpload, progressCallback);
      const fileKeys = uploaded.map((u) => u.key);
      const jobId = await createRafAssessment(activeAssessmentType, matterId, fileKeys, {
        representedParty: pendingOptions.representedParty,
        additionalContext: pendingOptions.additionalContext,
        newFileMetadata: uploaded.map((u) => ({ key: u.key, filename: u.filename })),
      });
      setActiveJobId(jobId);
      return `${ASSESSMENT_LABELS[activeAssessmentType]} started`;
    },
    [activeAssessmentType, matterId, pendingOptions],
  );

  const handleLoaderComplete = useCallback(() => {
    onRefresh();
  }, [onRefresh]);

  const handleLoaderClose = useCallback(() => {
    setLoaderOpen(false);
    setLoaderFiles([]);
    setActiveJobId('');
    setActiveAssessmentType(null);
    setPendingOptions({});
    onRefresh();
  }, [onRefresh]);

  const handleDownload = async (record: AssessmentRecord) => {
    try {
      const { downloadUrl } = await generateDownloadUrl(record.outputKey);
      window.open(downloadUrl, '_blank');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to download assessment.');
    }
  };

  const renderCard = (type: AssessmentType) => {
    const record = recordFor(type);
    const generatedDate = record
      ? new Date(record.generatedAt).toLocaleDateString('en-ZA', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        })
      : null;
    return (
      <Box
        key={type}
        data-testid={`assessment-card-${type}`}
        sx={{
          bgcolor: 'var(--bg)',
          border: '1px solid var(--border)',
          borderRadius: '12px',
          p: 2.5,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.25,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          {ASSESSMENT_ICONS[type]}
          <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: 'var(--t1)', flex: 1 }}>
            {ASSESSMENT_LABELS[type]}
          </Typography>
          {record && (
            <Typography
              sx={{
                fontSize: '0.7rem',
                color: 'var(--ok)',
                fontWeight: 600,
                bgcolor: 'color-mix(in srgb, var(--ok) 12%, transparent)',
                borderRadius: '6px',
                px: 1,
                py: 0.25,
              }}
            >
              GENERATED
            </Typography>
          )}
        </Box>
        <Typography sx={{ fontSize: '0.82rem', color: 'var(--t3)', lineHeight: 1.5 }}>
          {ASSESSMENT_DESCRIPTIONS[type]}
        </Typography>
        {record && generatedDate && (
          <Typography sx={{ fontSize: '0.74rem', color: 'var(--t4)' }}>
            Last generated {generatedDate}
            {record.sourceFiles && record.sourceFiles.length > 0 &&
              ` from ${record.sourceFiles.length} document${record.sourceFiles.length === 1 ? '' : 's'}`}
          </Typography>
        )}
        <Box sx={{ display: 'flex', gap: 1, mt: 'auto', pt: 0.5 }}>
          <Button
            size="small"
            variant="outlined"
            data-testid={`assessment-generate-${type}-btn`}
            onClick={() => setOpenModalFor(type)}
            sx={{
              textTransform: 'none',
              fontSize: '0.78rem',
              borderColor: 'var(--border)',
              color: 'var(--t2)',
              '&:hover': { borderColor: 'var(--teal)', color: 'var(--teal)' },
            }}
          >
            {record ? 'Regenerate' : 'Generate Assessment'}
          </Button>
          {record && (
            <Button
              size="small"
              variant="contained"
              data-testid={`assessment-download-${type}-btn`}
              onClick={() => handleDownload(record)}
              startIcon={<DownloadIcon sx={{ fontSize: 14 }} />}
              sx={{
                textTransform: 'none',
                fontSize: '0.78rem',
                bgcolor: 'var(--teal)',
                color: 'var(--bg)',
                '&:hover': { bgcolor: 'var(--teal-mid)' },
              }}
            >
              Download
            </Button>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Box>
      <Typography sx={{ fontSize: '0.92rem', fontWeight: 600, color: 'var(--t1)', mb: 0.5 }}>
        Assessments
      </Typography>
      <Typography sx={{ fontSize: '0.82rem', color: 'var(--t3)', mb: 2 }}>
        Generate a Merits or Quantum Assessment by uploading the supporting documents (police report, medical records, expert reports, payslips, etc.). Each assessment is produced as a downloadable Word document.
      </Typography>

      {error && (
        <Box
          sx={{
            bgcolor: 'color-mix(in srgb, var(--err) 10%, transparent)',
            border: '1px solid var(--err)',
            borderRadius: '8px',
            p: 1.25,
            mb: 2,
          }}
        >
          <Typography sx={{ fontSize: '0.8rem', color: 'var(--err)' }}>{error}</Typography>
        </Box>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
          gap: 2,
        }}
      >
        {renderCard('merits')}
        {renderCard('quantum')}
      </Box>

      <AssessmentUploadModal
        open={openModalFor !== null}
        assessmentType={openModalFor ?? 'merits'}
        onClose={() => setOpenModalFor(null)}
        onSubmit={(files, options) => {
          if (openModalFor) {
            handleStartGeneration(openModalFor, files, options);
          }
        }}
      />

      <UploadLoaderModal
        open={loaderOpen}
        files={loaderFiles}
        jobId={activeJobId}
        userId={userSub ?? undefined}
        onUpload={handleLoaderUpload}
        onComplete={handleLoaderComplete}
        onClose={handleLoaderClose}
      />

      {!loaderOpen && activeAssessmentType !== null && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, color: 'var(--t3)' }}>
          <CircularProgress size={14} sx={{ color: 'var(--teal)' }} />
          <Typography sx={{ fontSize: '0.78rem' }}>
            {ASSESSMENT_LABELS[activeAssessmentType]} processing...
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default AssessmentsTab;
